import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Send, Plus, FileText, Pencil } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, scoreColor, formatApiErrorDetail } from "../../lib/operatorWalk";
import ReportForm from "./ReportForm";

export default function AdminReports({ clients, onChanged }) {
  const { authHeader } = usePortalAuth();
  const [mode, setMode] = useState("list"); // list | new
  const [editing, setEditing] = useState(null); // full report doc when editing a draft
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/ow/reports`, { headers: authHeader });
      setReports(res.data.reports || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const publishDraft = async (id) => {
    setBusy(id);
    try {
      await axios.patch(`${OW_API}/admin/ow/reports/${id}`, { status: "published" }, { headers: authHeader });
      toast.success("Report published and client notified.");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    setBusy(id);
    try {
      await axios.delete(`${OW_API}/admin/ow/reports/${id}`, { headers: authHeader });
      toast.success("Report deleted.");
      load();
      onChanged?.();
    } catch {
      toast.error("Could not delete report.");
    } finally {
      setBusy("");
    }
  };

  const editDraft = async (id) => {
    setBusy(id);
    try {
      const res = await axios.get(`${OW_API}/admin/ow/reports/${id}`, { headers: authHeader });
      setEditing(res.data);
      setMode("new");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load draft.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex items-center gap-2 mb-5">
        <button
          onClick={() => { setMode("list"); setEditing(null); }}
          className={`px-4 py-2 text-sm font-medium border ${mode === "list" ? "bg-brand-700 text-white border-brand-700" : "bg-white text-brand-700 border-brand-200"}`}
          data-testid="reports-tab-list"
        >
          All reports
        </button>
        <button
          onClick={() => { setMode("new"); setEditing(null); }}
          className={`px-4 py-2 text-sm font-medium border inline-flex items-center gap-1.5 ${mode === "new" ? "bg-brand-700 text-white border-brand-700" : "bg-white text-brand-700 border-brand-200"}`}
          data-testid="reports-tab-new"
        >
          <Plus className="w-4 h-4" /> {editing ? "Editing draft" : "New report"}
        </button>
      </div>

      {mode === "new" ? (
        <div className="bg-white border border-brand-200 p-5 lg:p-6">
          {editing && (
            <div className="mb-4 text-[10px] uppercase tracking-[0.18em] text-brand-500" data-testid="report-editing-banner">
              Editing draft: {editing.month} / {editing.year}
            </div>
          )}
          <ReportForm
            key={editing?.id || "new"}
            clients={clients}
            existing={editing}
            knownNames={[...new Set(reports.map((r) => r.conducted_by).filter(Boolean))]}
            onSaved={(status) => {
              toast.success(status === "published" ? "Report published and client notified." : "Draft saved.");
              setMode("list");
              setEditing(null);
              load();
              onChanged?.();
            }}
          />
        </div>
      ) : loading ? (
        <div className="py-16 flex items-center justify-center text-ink-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading reports
        </div>
      ) : reports.length === 0 ? (
        <p className="text-ink-muted text-sm">No reports yet. Create your first report.</p>
      ) : (
        <div className="border border-brand-200 bg-white divide-y divide-brand-100" data-testid="reports-list">
          {reports.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`report-item-${r.id}`}>
              <div className="flex items-center gap-4 min-w-0">
                <FileText className="w-5 h-5 text-brand-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-brand-700 font-medium truncate">
                    {r.company_name} . {r.location_name}
                  </div>
                  <div className="text-xs text-ink-muted">
                    {r.month_label} . {r.scored_points}/20 scored{r.conducted_by ? ` . ${r.conducted_by}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-serif text-xl" style={{ color: scoreColor(r.percentage) }}>
                  {r.total}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${
                    r.status === "published" ? "bg-brand-100 text-brand-700" : "bg-amber-50 text-amber-700"
                  }`}
                  data-testid={`report-status-${r.id}`}
                >
                  {r.status}
                </span>
                {r.status === "draft" && (
                  <button
                    onClick={() => editDraft(r.id)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1.5 border border-brand-300 text-brand-700 hover:bg-brand-100 px-3 py-1.5 text-xs font-medium disabled:opacity-60"
                    data-testid={`report-edit-${r.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
                {r.status === "draft" && (
                  <button
                    onClick={() => publishDraft(r.id)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-3 py-1.5 text-xs font-medium disabled:opacity-60"
                    data-testid={`report-publish-${r.id}`}
                  >
                    {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Publish
                  </button>
                )}
                <button onClick={() => deleteReport(r.id)} disabled={busy === r.id} className="p-1.5 text-red-600 hover:bg-red-50" data-testid={`report-delete-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
